import { useState, useEffect } from "react";
import { Loader2, Save, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/admin/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { formatGs } from "@/lib/format";

const Field = ({ label, ...props }) => (
  <div><Label className="text-sm">{label}</Label><Input {...props} className="rounded-lg mt-1.5" /></div>
);

const Section = ({ title, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5">
    <h3 className="font-heading font-bold text-slate-900 mb-4">{title}</h3>
    <div className="space-y-4">{children}</div>
  </div>
);

const Settings = () => {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);

  const set = (k, v) => setS({ ...s, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        platform_name: s.platform_name, logo_text: s.logo_text, whatsapp: s.whatsapp, email: s.email,
        instagram: s.instagram, tiktok: s.tiktok, hero_title: s.hero_title, hero_subtitle: s.hero_subtitle,
        bank_details: s.bank_details, alias: s.alias, validation_mode: s.validation_mode,
        auto_validation_limit: Number(s.auto_validation_limit), currency: s.currency, primary_color: s.primary_color,
        auto_delivery: !!s.auto_delivery,
        brand_slogan: s.brand_slogan, services_title: s.services_title, services_subtitle: s.services_subtitle,
        howto_title: s.howto_title, howto_subtitle: s.howto_subtitle, faq_title: s.faq_title,
        cta_title: s.cta_title, cta_subtitle: s.cta_subtitle, footer_tagline: s.footer_tagline,
        refill_url: s.refill_url,
      };
      await api.put("/admin/settings", payload);
      toast.success("Configuración guardada");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  if (!s) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>;

  const resetData = async () => {
    if (!window.confirm("¿Borrar TODOS los pedidos, clientes, comprobantes, suscripciones, tráfico, costos y actividad? Los servicios, cupones, proveedores y administradores NO se borran. Esta acción no se puede deshacer.")) return;
    if (!window.confirm("Confirmá una vez más: se eliminarán definitivamente los datos de operación.")) return;
    try {
      const { data } = await api.post("/admin/maintenance/reset-transactional");
      const total = Object.values(data.deleted).reduce((a, b) => a + b, 0);
      toast.success(`Datos borrados (${total} registros). El próximo pedido será #1048.`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Configuración" subtitle="Datos de la plataforma, pagos y validación"
        action={<Button onClick={save} disabled={saving} className="rounded-lg bg-indigo-600 hover:bg-indigo-700" data-testid="save-settings">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Guardar</>}</Button>} />

      <div className="grid lg:grid-cols-2 gap-4">
        <Section title="Plataforma">
          <Field label="Nombre de la plataforma" value={s.platform_name || ""} onChange={(e) => set("platform_name", e.target.value)} data-testid="set-name" />
          <Field label="Texto del logo" value={s.logo_text || ""} onChange={(e) => set("logo_text", e.target.value)} />
          <Field label="Color primario" value={s.primary_color || ""} onChange={(e) => set("primary_color", e.target.value)} />
          <Field label="Moneda" value={s.currency || ""} onChange={(e) => set("currency", e.target.value)} />
        </Section>

        <Section title="Contacto y redes">
          <Field label="WhatsApp" value={s.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} data-testid="set-whatsapp" />
          <Field label="Email" value={s.email || ""} onChange={(e) => set("email", e.target.value)} />
          <Field label="Instagram" value={s.instagram || ""} onChange={(e) => set("instagram", e.target.value)} />
          <Field label="TikTok" value={s.tiktok || ""} onChange={(e) => set("tiktok", e.target.value)} />
        </Section>

        <Section title="Textos principales">
          <Field label="Eslogan (badge del hero)" value={s.brand_slogan || ""} onChange={(e) => set("brand_slogan", e.target.value)} />
          <Field label="Título del hero" value={s.hero_title || ""} onChange={(e) => set("hero_title", e.target.value)} />
          <div><Label className="text-sm">Subtítulo del hero</Label><Textarea value={s.hero_subtitle || ""} onChange={(e) => set("hero_subtitle", e.target.value)} className="rounded-lg mt-1.5" /></div>
        </Section>

        <Section title="Textos de las secciones">
          <Field label="Título · Servicios" value={s.services_title || ""} onChange={(e) => set("services_title", e.target.value)} />
          <Field label="Subtítulo · Servicios" value={s.services_subtitle || ""} onChange={(e) => set("services_subtitle", e.target.value)} />
          <Field label="Título · Cómo funciona" value={s.howto_title || ""} onChange={(e) => set("howto_title", e.target.value)} />
          <Field label="Subtítulo · Cómo funciona" value={s.howto_subtitle || ""} onChange={(e) => set("howto_subtitle", e.target.value)} />
          <Field label="Título · Preguntas frecuentes" value={s.faq_title || ""} onChange={(e) => set("faq_title", e.target.value)} />
          <Field label="Título · CTA final" value={s.cta_title || ""} onChange={(e) => set("cta_title", e.target.value)} />
          <Field label="Subtítulo · CTA final" value={s.cta_subtitle || ""} onChange={(e) => set("cta_subtitle", e.target.value)} />
          <div><Label className="text-sm">Texto del pie de página</Label><Textarea value={s.footer_tagline || ""} onChange={(e) => set("footer_tagline", e.target.value)} className="rounded-lg mt-1.5" /></div>
        </Section>

        <Section title="Métodos de pago">
          <Field label="Alias / Billetera" value={s.alias || ""} onChange={(e) => set("alias", e.target.value)} data-testid="set-alias" />
          <div><Label className="text-sm">Datos bancarios</Label><Textarea value={s.bank_details || ""} onChange={(e) => set("bank_details", e.target.value)} className="rounded-lg mt-1.5" /></div>
        </Section>

        <Section title="Validación de pagos">
          <RadioGroup value={s.validation_mode} onValueChange={(v) => set("validation_mode", v)} className="space-y-2">
            {[["manual", "Manual — el admin revisa todos los comprobantes"], ["automatica", "Automática — la IA analiza y aprueba"], ["automatica_revision", "Automática + revisión en casos sospechosos"]].map(([val, label]) => (
              <label key={val} className="flex items-center gap-3 rounded-lg border border-slate-200 p-3 cursor-pointer hover:border-indigo-300">
                <RadioGroupItem value={val} data-testid={`vmode-${val}`} />
                <span className="text-sm text-slate-700">{label}</span>
              </label>
            ))}
          </RadioGroup>
          <div>
            <Label className="text-sm">Límite para validación automática (₲)</Label>
            <Input type="number" value={s.auto_validation_limit || 0} onChange={(e) => set("auto_validation_limit", e.target.value)} className="rounded-lg mt-1.5" data-testid="set-limit" />
            <p className="text-xs text-slate-400 mt-1">Pedidos hasta {formatGs(s.auto_validation_limit || 0)} pueden aprobarse automáticamente. Los mayores van a revisión manual.</p>
          </div>
        </Section>

        <Section title="Entrega automática">
          <label className="flex items-center justify-between rounded-lg border border-slate-200 p-3 cursor-pointer">
            <span className="text-sm text-slate-700">Enviar pedidos al proveedor automáticamente al confirmarse el pago</span>
            <Switch checked={!!s.auto_delivery} onCheckedChange={(v) => set("auto_delivery", v)} data-testid="set-auto-delivery" />
          </label>
          <p className="text-xs text-slate-400">Requiere un proveedor configurado en la sección <strong>Proveedores</strong> y cada paquete mapeado a un ID de servicio. Si está desactivado, podés enviar cada pedido manualmente desde <strong>Pedidos</strong>.</p>
        </Section>
        <Section title="Reposición y soporte">
          <Field label="URL de reposición (proveedor)" value={s.refill_url || ""} onChange={(e) => set("refill_url", e.target.value)} data-testid="set-refill" placeholder="https://refil.perfilturbinado.com/" />
          <p className="text-xs text-slate-400">A dónde se dirige al cliente cuando pide reposición por caída de seguidores (página de Soporte y footer).</p>
        </Section>

        <Section title="Zona de mantenimiento">
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-4">
            <p className="text-sm font-semibold text-rose-700 flex items-center gap-1.5"><AlertTriangle className="h-4 w-4" /> Borrar datos de operación</p>
            <p className="text-xs text-rose-600/90 mt-1">Elimina pedidos, clientes, comprobantes, suscripciones, tráfico, costos y actividad. Ideal para empezar limpio en producción. No borra servicios, cupones, proveedores ni administradores.</p>
            <Button onClick={resetData} className="mt-3 rounded-lg bg-rose-600 hover:bg-rose-700 text-white" data-testid="reset-data-btn"><Trash2 className="h-4 w-4 mr-1.5" /> Borrar datos de prueba</Button>
          </div>
        </Section>
      </div>
    </div>
  );
};

export default Settings;
